import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type {
  InventoryQuery,
  CreateInventoryItemInput,
  UpdateInventoryItemInput,
} from '@siliconvault/shared';
import { useVault } from '../context/VaultContext';

export function useInventoryList(query: Partial<InventoryQuery> = {}) {
  const { activeVaultId } = useVault();
  const queryWithVault = activeVaultId ? { ...query, vaultId: activeVaultId } : query;

  return useQuery({
    queryKey: ['inventory', queryWithVault],
    queryFn: () => apiClient.listInventory(queryWithVault),
  });
}

export function useInventoryItem(itemId: string) {
  return useQuery({
    queryKey: ['inventory', itemId],
    queryFn: () => apiClient.getInventoryItem(itemId),
    enabled: !!itemId,
  });
}

export function useCreateInventoryItem() {
  const queryClient = useQueryClient();
  const { activeVaultId } = useVault();

  return useMutation({
    mutationFn: (data: CreateInventoryItemInput) =>
      apiClient.createInventoryItem(data, activeVaultId ?? undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useUpdateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: UpdateInventoryItemInput }) =>
      apiClient.updateInventoryItem(itemId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({
        queryKey: ['inventory', variables.itemId],
      });
    },
  });
}

export function useDeleteInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => apiClient.deleteInventoryItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useToggleShowroom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, showcase }: { itemId: string; showcase?: boolean }) =>
      apiClient.toggleShowroom(itemId, showcase),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', variables.itemId] });
      queryClient.invalidateQueries({ queryKey: ['showroom'] });
    },
  });
}

export function useShowroomList(query: Record<string, string> = {}) {
  return useQuery({
    queryKey: ['showroom', query],
    queryFn: () => apiClient.listShowroom(query),
  });
}

export function useShowroomTicker(vaultType?: string) {
  return useQuery({
    queryKey: ['showroom', 'ticker', vaultType],
    queryFn: () => apiClient.getShowroomTicker(vaultType),
    refetchInterval: 60000,
  });
}

export function useAnalyzeImage() {
  const queryClient = useQueryClient();
  const { activeVault } = useVault();
  return useMutation({
    mutationFn: (file: File | Blob) => apiClient.analyzeImage(file, undefined, activeVault?.vaultType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-mode'] });
    },
  });
}

export function useUploadItemImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, file }: { itemId: string; file: File | Blob }) =>
      apiClient.uploadItemImage(itemId, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['itemImages', variables.itemId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', variables.itemId] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}
